/**
 * @file src/components/UserControls/UserControls.tsx
 * @exports UserControls
 * @summary Header controls with two separate targets: the organization button, which opens the
 * organization search dropdown, and the avatar/name/caret, which opens the profile menu.
 * Organization changes warn only when recipient data would be discarded. The profile menu holds
 * only Profile Settings and sign-out; organizations are reached from the left nav.
 */
import { navigateApp } from "src/lib/app-router";
import { useOrganizationAccountPath } from "src/hooks/useOrganizationAccountPath";
import { useLocation, useParams } from "@tanstack/react-router";
import {
	pressedEnter,
	Avatar,
	BodySSemiBold,
	CaretDown,
	colors,
	Truncate,
	useMeasureWatchResize,
	User,
	UserCardModal,
	type UserCardOptionsType,
} from "@cerebruminc/cerebellum";
import { Button, Skeleton, useMantineTheme } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import React, {
	type CSSProperties,
	type FC,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useState,
	useMemo,
} from "react";
import { styled } from "styled-components";

import { OrganizationSearchDropdown } from "src/components/OrganizationSearchDropdown";
import { currentOrgIdAtom, windowSizeAtom } from "src/shame/shame-atoms";
import { ALLOWED_USER_PATHS, paths } from "src/const";
import { SortOrder, useMeQuery, useOrganizationsQuery } from "src/generated/graphql";
import { ALL_ORGS_TEST_ID, getOrgSelectorTestId, hasSuperAdminRole, getTextWidth } from "src/helpers";
import { useHandleLogout, useUpdateSelectedOrganizationId } from "src/hooks";

import { UserControlsBase, AvatarBox, CaretBox, OrgSwitcherBox, ProfileTrigger } from "./UserControlsStyles";
import type { UserControlsType } from "./types";
import { SIDEBAR_WIDTH } from "./../Layouts/PageLayout/PageLayoutStyles";

const ALL_ORGS_LABEL = "All Organizations";
/*
 * The skeleton stands in for the button, so it is as tall as the button: the height the cerebellum
 * Mantine theme gives a small button, not Mantine's own 36px default for that size.
 */
const ORG_BUTTON_HEIGHT = 32;
const ORG_SKELETON_WIDTH = 160;
// React's CSSProperties has no index signature, so a custom property needs declaring to be set.
type OrgButtonStyle = CSSProperties & { "--button-hover"?: string };
const ELEMENTS_GAP = 66,
	PADDING_SUM = 58,
	CARET_SIZE = 20,
	NAVBAR_PADDING = 40,
	TEXT_SPACE = 50;

const OrgHeaderLogo = styled.img`
  height: 16px;
  width: auto;
  display: inline-block;
  margin-right: 10px;
`;
/*
 * No cursor or pointer-events of its own. Both were here when this sat inside the profile trigger
 * and had to stay hit-testable through it; the name now lives inside the organization button, which
 * owns the cursor — and a single-organization label must not claim a pointer for a click that does
 * nothing.
 */
const OrgNameBox = styled.div<{ $isLargeText: boolean; $sizeAdjustment: number }>`
  display: inline-block;
  max-width: 600px;
  width: auto;
  ${({ $isLargeText, $sizeAdjustment }) =>
		$isLargeText &&
		`  @media all and (max-width: calc(600px + ${$sizeAdjustment}px)) {
    width: calc(100vw - ${$sizeAdjustment}px);
  }`}
`;
const Name = styled(BodySSemiBold)`
  max-width: 400px;
  @media all and (max-width: 900px) {
    max-width: 300px;
  }
  @media all and (max-width: 800px) {
    max-width: 200px;
  }
`;

/** Header organization switcher and profile menu. */
export const UserControls: FC<UserControlsType> = ({ white }) => {
	// UserCard
	const [showUserCard, setShowUserCard] = useState(false);
	const [avatarRef, avatarMeasure] = useMeasureWatchResize();
	const [usernameRef, usernameMeasure] = useMeasureWatchResize();
	const { width } = useAtomValue(windowSizeAtom);

	const { data: userQueryData } = useMeQuery();
	const userData = userQueryData?.me;
	const { email, firstName, lastName } = userData || {};
	const updateSelectedOrganizationId = useUpdateSelectedOrganizationId();
	const [currentOrgId, setCurrentOrgId] = useAtom(currentOrgIdAtom);
	const handleLogout = useHandleLogout();
	const location = useLocation();
	const params = useParams({ strict: false }) as Record<string, string>;
	const getOrganizationAccountPath = useOrganizationAccountPath();
	const [showOrgSearch, setShowOrgSearch] = useState(false);
	const theme = useMantineTheme();

	// A two-row probe is enough to auto-select a single-org user and to decide
	// whether org switching is meaningful, without pulling the full dataset.
	const {
		data: probeData,
		isError: isProbeError,
		isLoading: isLoadingProbe,
	} = useOrganizationsQuery({
		orderBy: [{ name: SortOrder.Asc }, { id: SortOrder.Asc }],
		where: { deletedAt: { equals: null } },
		take: 2,
	});
	const probeOrgs = useMemo(() => probeData?.organizations ?? [], [probeData]);
	// A failed probe knows nothing about how many organizations there are, so it must not be
	// the reason a user cannot switch. A genuine one-org account still gets the plain label.
	// Kept a strict boolean: it decides whether the switcher is rendered as a button or as a plain
	// label, and an undefined would leave a focusable button with no name and nothing to do.
	const canSwitchOrgs = probeOrgs.length > 1 || Boolean(isProbeError);

	// The active organization is not guaranteed to be in the probe, so fetch it
	// by id for the header display.
	const { data: activeOrgData, isInitialLoading: isLoadingActiveOrgLookup } = useOrganizationsQuery(
		{ where: { id: { equals: currentOrgId ?? "" } }, take: 1 },
		{ enabled: !!currentOrgId && !isLoadingProbe && !probeOrgs.some((org) => org.id === currentOrgId) },
	);
	const activeOrgFromQuery = activeOrgData?.organizations?.[0];

	// If the user only has access to one organization, set that as the current org
	useEffect(() => {
		if (userData && !isLoadingProbe && probeOrgs.length === 1) {
			setCurrentOrgId(probeOrgs[0].id ?? null);
		}
	}, [userData, isLoadingProbe, probeOrgs, setCurrentOrgId]);

	const userName = `${firstName || ""} ${lastName || ""}`;
	const lastInitial = lastName?.charAt(0) || "";
	const shortUserName = `${firstName || ""} ${!firstName ? lastName || "" : lastInitial ? `${lastInitial}.` : ""}`;

	const handleOrgChange = useCallback(
		async (orgId: string | null) => {
			if (
				location.pathname === paths.createInvite &&
				document.getElementById("invite-recipient")?.dataset.unsentInvites === "true" &&
				!window.confirm("Changing organizations will clear your unsent invites. Continue?")
			)
				return;
			setCurrentOrgId(orgId);
			if (userData?.id) {
				try {
					const { updateOneUser: response } = await updateSelectedOrganizationId({
						id: userData.id,
						selectedOrganizationId: orgId,
					});

					if (!response) {
						throw new Error("Failed to update user selected organization id");
					}

					// Super admins get access to everything, but don't always have a currentRole listed
					const currentRole = response.roleAssignment?.find((item: any) => item.organizationId === orgId)?.roleV2?.name;
					const isSuperAdmin = hasSuperAdminRole(userData);
					const isBaseUser = (!currentRole || currentRole === "USER") && !isSuperAdmin;

					const restrictedRoute = !ALLOWED_USER_PATHS.has(location.pathname);

					// redirect users to a usable path
					if (restrictedRoute && isBaseUser) {
						navigateApp({ href: paths.home });
					} else if (
						typeof params.id === "string" &&
						params.id.toLowerCase() !== "create" &&
						location.pathname === `${paths.organizations}/${encodeURIComponent(params.id)}`
					) {
						// The hook resolves the short name by id, which is what the removed full
						// organization list was being searched for.
						navigateApp({ href: orgId ? await getOrganizationAccountPath({ id: orgId }) : paths.organizations });
					}
				} catch (error) {
					console.error("Failed to update selected organization id", error);
				}
			}
		},
		[getOrganizationAccountPath, location.pathname, params.id, setCurrentOrgId, updateSelectedOrganizationId, userData],
	);

	const activeOrg =
		probeOrgs.find((org) => org.id === currentOrgId) ?? (activeOrgFromQuery?.id ? activeOrgFromQuery : undefined);
	const activeOrgTestId = activeOrg ? getOrgSelectorTestId(activeOrg) : ALL_ORGS_TEST_ID;
	// Until both lookups settle there is no way to tell an "All Organizations" user from one
	// whose organization simply has not arrived, so show neither label yet.
	const isLoadingActiveOrg = isLoadingProbe || isLoadingActiveOrgLookup;
	const activeOrgNameSize = useMemo(
		() =>
			getTextWidth({
				text: activeOrg?.name || "",
				font: `15px "Nunito Sans", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"`,
			}),
		[activeOrg?.name],
	);

	/*
	 * Three plain properties on the stock button.
	 *
	 * A single-organization label is not a control, so it must neither claim a pointer nor answer to
	 * the hover that every button gets. Mantine exempts only `:disabled` and `[data-disabled]` from
	 * that hover rule, and a `div` root matches neither — so the white variant's tint would still
	 * fire on a label that does nothing. Pointing the hover at the button's own background is what
	 * makes it a no-op, and stays correct if the variant ever changes. `data-disabled` would silence
	 * it too, but it also paints the Mantine disabled palette: this is a plain label, not a control
	 * that has been turned off.
	 *
	 * And Mantine rings focus in the primary blue, which all but disappears against the
	 * blue-to-purple gradient header — white is the only ring that reads there, and an inline colour
	 * is what outranks the shorthand in Mantine's `:focus-visible` rule. Only the colour is set
	 * here: the ring's 1px width comes from cerebellum's mantineGlobalStyles.css, imported at the
	 * app root, so the two do not fight.
	 */
	const orgButtonStyle: OrgButtonStyle = {
		...(canSwitchOrgs ? {} : { cursor: "default", "--button-hover": "var(--button-bg)" }),
		...(white ? {} : { outlineColor: colors.WHITE }),
	};

	const userCardOptions: UserCardOptionsType[] = [
		{
			text: "Profile Settings",
			Icon: User,
			onClick: () => navigateApp({ href: paths.profileSettings }),
		},
	];

	const sizeAdjustment =
		SIDEBAR_WIDTH +
		usernameMeasure.width +
		avatarMeasure.width +
		ELEMENTS_GAP +
		PADDING_SUM +
		NAVBAR_PADDING +
		CARET_SIZE +
		TEXT_SPACE;

	return (
		<UserControlsBase $white={white}>
			{!!userData && (
				<OrganizationSearchDropdown
					activeOrgId={currentOrgId}
					onClose={() => setShowOrgSearch(false)}
					onSelect={({ organizationId }) => {
						setShowOrgSearch(false);
						// Re-picking the organization already selected is a no-op, not another mutation.
						if (organizationId !== currentOrgId) handleOrgChange(organizationId);
					}}
					opened={showOrgSearch}
				>
					<OrgSwitcherBox>
						{isLoadingActiveOrg ? (
							<Skeleton
								data-testid="org-selector-loading"
								height={ORG_BUTTON_HEIGHT}
								radius={8}
								width={ORG_SKELETON_WIDTH}
							/>
						) : (
							<Button
								aria-expanded={canSwitchOrgs ? showOrgSearch : undefined}
								aria-haspopup={canSwitchOrgs ? "dialog" : undefined}
								aria-label={
									canSwitchOrgs
										? `Switch organization. Current selection: ${activeOrg?.name ?? ALL_ORGS_LABEL}`
										: undefined
								}
								boxed
								color="gray"
								// A single-organization user has nothing to switch to, so the label is a
								// label: no caret, no pointer, and nothing to activate.
								component={canSwitchOrgs ? "button" : "div"}
								data-testid="org-selector-trigger"
								onClick={canSwitchOrgs ? () => setShowOrgSearch((isOpen) => !isOpen) : undefined}
								rightSection={canSwitchOrgs ? <CaretDown fill={theme.colors.gray[5]} /> : undefined}
								size="sm"
								style={orgButtonStyle}
								/*
								 * Cortex can leave this white because its header is white throughout. Cognition's is
								 * not: on the white header a white button disappears, so it keeps the grey it has
								 * always had — `light` on the gray scale is COOL_GREY_4, the same value the old
								 * BoxedButton took from the CoolGrey family's `light`. The gradient header still gets
								 * the white pill, which is what read there before too.
								 */
								variant={white ? "light" : "white"}
							>
								{activeOrg ? (
									<>
										{activeOrg?.logoUrl && <OrgHeaderLogo src={activeOrg.logoUrl} alt={`${activeOrg.name} logo`} />}
										<OrgNameBox
											data-testid={activeOrgTestId}
											$sizeAdjustment={sizeAdjustment}
											$isLargeText={activeOrgNameSize + sizeAdjustment >= width}
										>
											<Truncate>{activeOrg?.name ?? " "}</Truncate>
										</OrgNameBox>
									</>
								) : (
									<span data-sentry-unmask data-testid={ALL_ORGS_TEST_ID}>
										{ALL_ORGS_LABEL}
									</span>
								)}
							</Button>
						)}
					</OrgSwitcherBox>
				</OrganizationSearchDropdown>
			)}
			<UserCardModal
				opened={showUserCard}
				onClose={() => setShowUserCard(false)}
				onSignOut={handleLogout}
				signOutTestId="user-menu-signout"
				topOffset={70}
				userEmail={email ?? ""}
				userName={userName}
				options={userCardOptions}
			>
				<ProfileTrigger
					onClick={() => setShowUserCard(true)}
					onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
						if (pressedEnter(event) || event.key === " ") {
							setShowUserCard(true);
							event.preventDefault(); // prevent scroll on spacebar
						}
					}}
					$clickable={!showUserCard}
					tabIndex={!showUserCard ? 0 : -1}
					// aria-label is prohibited on a generic element: without a role the name is dropped.
					role="button"
					aria-label="Open user settings panel"
					data-testid="user-menu-trigger"
				>
					<AvatarBox ref={avatarRef} $white={white}>
						<Avatar
							placeholderInitials={userName}
							imageUrl={!userName && activeOrg?.logoUrl ? activeOrg.logoUrl : undefined}
							backgroundGradient={
								!userName && activeOrg?.logoUrl
									? "transparent"
									: white
										? undefined
										: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0) 80%)"
							}
							themeOverride={{ size: 50 }}
						/>
					</AvatarBox>
					{userData && (
						<Name ref={usernameRef} $textColor={white ? colors.COOL_GREY_100 : colors.WHITE}>
							<Truncate>{shortUserName}</Truncate>
						</Name>
					)}
					<CaretBox $white={white}>
						<CaretDown fill={colors.COOL_GREY_30} />
					</CaretBox>
				</ProfileTrigger>
			</UserCardModal>
		</UserControlsBase>
	);
};
